import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '../common/tooltip/ToolTip';
import NavigationIcons from '../navigation/components/NavigationIcons';
import { useAppSelector } from '../../hooks/storeHooks';
import './GroupStatus.scss';

interface CustomTableProps {
  rawData: any;
  index: number;
  row: any;
  rowClassName: string;
  style: React.CSSProperties;
  href: string;
  handleClick: any;
  customViews?: boolean;
}

const GroupStatusRow: React.FC<CustomTableProps> = ({
  rawData,
  row,
  rowClassName,
  style,
  href,
  handleClick,
  customViews = false,
}) => {
  const navigate = useNavigate();

  // Get asset groups from Redux state to find industryApplicationId (only when in custom views)
  const assets = useAppSelector((state) => state.assetGroups?.assets);

  // Helper function to find industryApplicationId by well name
  const findIndustryApplicationIdByWellName = (wellName: string): number => {
    if (!wellName || !customViews) return 0;

    if (assets) {
      const asset = assets.find((asset: any) => asset.assetName === wellName);
      if (asset?.industryApplicationId) {
        return asset.industryApplicationId;
      }
    }

    return 0; // Default fallback
  };

  const handleAnchorClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      navigate(href);
    },
    [navigate, href],
  );

  return (
    <div onClick={handleClick} className={rowClassName} style={style}>
      {row.cells.map((cell: any, cellIndex: any) => {
        const isLastCell = row.cells.length - 2 === cellIndex || row.cells.length - 3 === cellIndex;
        const wellCell = row.cells.find((column: any) => column.column.Header === 'Well');
        const wellName = wellCell ? wellCell.value : undefined;
        const rowRawData = rawData?.find?.((row: any) => row?.Well?.value === wellName) ?? {};

        if (cell.column.Header !== 'Assetguid') {
          let className = 'td table-row-general';
          let cellValue = cell.render('Cell');
          const cellDetails = rowRawData?.[cell.column.id] ?? {};

          switch (cell.column.id.toLowerCase()) {
            case 'well':
              className = `td table-row-name ${rowClassName}`;
              if (customViews) {
                const wellName = cell.value;
                const industryApplicationId = findIndustryApplicationIdByWellName(wellName);
                const asset = { industryApplicationId };

                cellValue = (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <NavigationIcons asset={asset} isActive={false} />
                    <a key={cellIndex} href='#' className='table-row-link' onClick={handleAnchorClick}>
                      <span style={{ color: '#60bfda', wordBreak: 'break-all' }}>{wellName}</span>
                    </a>
                  </div>
                );
              } else {
                cellValue = (
                  <a key={cellIndex} href='#' className='table-row-link' onClick={handleAnchorClick}>
                    {cell.render('Cell')}
                  </a>
                );
              }
              break;
            case '7':
              cellValue = (
                <div key={cellIndex} className='alarm-badges'>
                  {cell.value?.map((alarm: string, alarmIndex: number) => (
                    <div key={alarmIndex} className='badge'>
                      <span className='badge-text'>{alarm}</span>
                    </div>
                  ))}
                </div>
              );
              break;
            default:
              break;
          }

          if (cellDetails.backColor && cell.value) {
            cellValue = (
              <div key={cellIndex} className='alarm-badges'>
                <div
                  className='badge'
                  style={{
                    backgroundColor: 'transparent',
                    color: cellDetails.backColor,
                    border: `1px solid ${cellDetails.backColor}`,
                  }}
                >
                  {cell.render('Cell')}
                </div>
              </div>
            );
          }

          if (!cellDetails?.showToolTip) {
            return (
              <div
                {...cell.getCellProps()}
                className={`table-row-value ${customViews && cellIndex === 0 ? 'table-row-first' : className}
                ${cell.column.Header === 'Well' ? 'well' : ''}`}
              >
                {cellValue}
              </div>
            );
          } else {
            const tooltipDirection = isLastCell ? 'Left' : 'right';

            const toolTipText = cellDetails.value;

            return (
              <div
                {...cell.getCellProps()}
                className={`table-row-value ${customViews && cellIndex === 0 ? 'table-row-first' : className}`}
                style={{ alignItems: 'center' }}
              >
                <Tooltip content={toolTipText} direction={tooltipDirection} isGroupStatus={true}>
                  {cellValue}
                </Tooltip>
              </div>
            );
          }
        }
        return null;
      })}
    </div>
  );
};

export default React.memo(GroupStatusRow);
